import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import usePageTracking from "../../hooks/usePageTracking";
import {
  activityStart,
  activityEnd,
} from "../../redux/slices/trackActivitySlice";
import { useNavigate, useLocation } from "react-router-dom";
import Select, { components } from "react-select";
import {
  getRoles,
  getAllStates,
  getAllCities,
} from "../../redux/slices/authSlice";
import { allowedNames, allowedRolesForFree } from "../../constants";
import { filterCity } from "../../redux/slices/searchSlice";
import searchService from "../../api/searchServices";
import mortgageScenarioService from "../../api/mortgageScenarioService";
import ComparisonModal from "../restriction-modals/ComparisonModal";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import { UserRound } from "lucide-react";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getMortgageTourSteps from "./mortgageTourSteps";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../utils/profileCompletion";
import "./mortgage.scss";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect the mortgage defaults/comparison.
const MORTGAGE_RELEVANT_PROFILE_NUDGES = [
  {
    key: "role",
    message: "Add your nursing role to auto-fill accurate mortgage defaults.",
  },
  {
    key: "city",
    message: "Add your city and state to auto-fill your mortgage defaults.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to see what you can actually afford.",
  },
];

const DEFAULT_CITY_DATA = {
  median_home_price: 0,
  monthly_rent: 0,
  gross_annual_salary: 0,
  net_annual_salary: 0,
  tax_rate: 0,
};

const Icon = {
  chevD: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  home: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  ext: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
    </svg>
  ),
  bookmark: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  ),
  lock: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  info: (p = {}) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-5" />
      <path d="M12 8h.01" />
    </svg>
  ),
};

/* ── Info tooltip — small (i) icon. Self-contained (inline styles) so it
   doesn't depend on mortgage.scss classes. Desktop (real mouse) hovers
   in/out; touch devices tap-to-toggle + tap-outside-to-close (hover alone
   flickers open/closed on tap — mobile browsers synthesize a
   mouseenter+mouseleave pair before the click lands). ─────────────────── */
const isHoverCapable = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Tooltip box width — clamped further per-viewport in reposition() below.
const INFO_TIP_WIDTH = 220;
const INFO_TIP_MARGIN = 12;

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const hoverCapable = useRef(isHoverCapable()).current;

  // Anchors the popup to the icon's actual screen position (fixed, not
  // relative to the wrapper) and clamps it so it never spills past the
  // left/right edge of the viewport — the old centered-under-icon
  // placement clipped off-screen for icons near a narrow column's edge.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(INFO_TIP_WIDTH, vw - INFO_TIP_MARGIN * 2);
    const iconCenter = rect.left + rect.width / 2;
    const left = Math.max(
      INFO_TIP_MARGIN,
      Math.min(iconCenter - width / 2, vw - width - INFO_TIP_MARGIN),
    );
    const arrowLeft = Math.max(10, Math.min(iconCenter - left, width - 10));
    setPos({ left, bottom: window.innerHeight - rect.top + 8, width, arrowLeft });
  };

  useLayoutEffect(() => {
    if (!show) return undefined;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [show]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={hoverCapable ? () => setShow(false) : undefined}
    >
      <Icon.info
        style={{ width: 14, height: 14, color: "var(--mmp-text-secondary)", cursor: "pointer" }}
        aria-label="More info"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShow((s) => !s);
        }}
      />
      {show && pos && (
        <div
          role="tooltip"
          style={{
            position: "fixed",
            left: pos.left,
            bottom: pos.bottom,
            background: "#0f172a",
            color: "#fff",
            fontSize: 12,
            fontWeight: 400,
            textTransform: "none",
            letterSpacing: "normal",
            lineHeight: 1.4,
            padding: "8px 10px",
            borderRadius: 8,
            width: pos.width,
            zIndex: 2000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
          }}
        >
          {text}
          <div
            style={{
              position: "absolute",
              bottom: -4,
              left: pos.arrowLeft,
              transform: "translateX(-50%) rotate(45deg)",
              width: 8,
              height: 8,
              background: "#0f172a",
            }}
          />
        </div>
      )}
    </span>
  );
};

function MoneyInput({ label, value, onChange, prefix = "$", dataTour }) {
  return (
    <div data-tour={dataTour}>
      <div className="m-label">{label}</div>
      <div className="m-money">
        <span className="m-money-prefix">{prefix}</span>
        <input
          type="text"
          value={value.toLocaleString()}
          onChange={(e) => {
            const v = parseInt(e.target.value.replace(/[^0-9]/g, "")) || 0;
            onChange(v);
          }}
          className="m-money-input"
        />
      </div>
    </div>
  );
}

export default function MortgageCalculator() {
  const dispatch = useDispatch();

  // Guided tour — auto-starts on first visit, replayable from the header.
  const mortgageTourSteps = useMemo(() => getMortgageTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("mortgage");

  // Track Activity
  usePageTracking("MORTGAGE_CALCULATOR");
  useEffect(() => {
    dispatch(
      activityStart({ page_name: "MORTGAGE_CALCULATOR", platform: "web" }),
    );
    return () => dispatch(activityEnd({ page_name: "MORTGAGE_CALCULATOR" }));
  }, [dispatch]);
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useSelector((s) => s.auth);
  const profileStore = useSelector((s) => s.profile);
  const profileData = profileStore?.profileItem?.data;

  // "Complete your profile" nudge shown above the title, in its own card —
  // picks the single highest-priority missing field rather than a generic
  // percentage, since that's more actionable here. Dismissal is in-memory
  // only (not persisted) so the nudge reappears on reload, re-login, or a
  // fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const profileCompletionPercent = getProfileCompletionPercent(profileData);
  const activeProfileNudge = profileData
    ? MORTGAGE_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(profileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  const fetchOnceRef = useRef(false);
  const autoLoadedRef = useRef(false);
  const profileInitializedRef = useRef(false);

  const [role, setRole] = useState(() => profileData?.nursing_role?.id || 9);
  const [state, setState] = useState(
    () => profileData?.living_state_info?.state_full_name || "California",
  );
  const [city, setCity] = useState(() => profileData?.city || "Sacramento");
  const [price, setPrice] = useState(0);
  const [dpPct, setDpPct] = useState(0);
  const [term, setTerm] = useState(30);
  const [rate, setRate] = useState(7.065);
  const [toast, setToast] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [scenarioPlan, setScenarioPlan] = useState({
    plan_slug: "free",
    plan_limit: 1,
    total_saved: 0,
    over_plan_count: 0,
  });
  const [activeScenario, setActiveScenario] = useState(null);
  const [scenarioSaving, setScenarioSaving] = useState(false);
  const skipPriceResetRef = useRef(false);

  const [cityData, setCityData] = useState(DEFAULT_CITY_DATA);
  const [roleOptions, setRoleOptions] = useState([]);
  const [apiStates, setApiStates] = useState([]);
  const [apiCities, setApiCities] = useState([]);
  const [upgradeSource, setUpgradeSource] = useState(null);
  const [subAlertCompModalVisible, setSubAlertCompModalVisible] = useState(
    false,
  );
  const [subText, setSubText] = useState({
    header: "",
    desc: "",
    massage1: "",
    pluspricem: "",
    pluspricey: "",
    desc2: "",
    promassage: "",
    propricem: "",
    propricey: "",
  });

  useEffect(() => {
    if (!fetchOnceRef.current) {
      fetchOnceRef.current = true;
      dispatch(getRoles());
      dispatch(getAllStates());
    }
  }, [dispatch]);

  useEffect(() => {
    if (profileInitializedRef.current) return;
    if (location.state?.scenarioId) return;
    const data = profileStore?.profileItem?.data;
    if (!data) return;
    profileInitializedRef.current = true;
    if (data?.nursing_role?.id) setRole(data.nursing_role.id);
    if (data?.living_state_info?.state_full_name)
      setState(data.living_state_info.state_full_name);
    if (data?.city) setCity(data.city);
  }, [profileStore?.profileItem?.data, location.state?.scenarioId]);

  useEffect(() => {
    const list =
      authStore?.stateItems?.data?.states ||
      authStore?.stateItems?.states ||
      [];
    if (list.length > 0) {
      setApiStates(
        list.map((s) => ({ name: s.state_full_name, code: s.state })),
      );
    }
  }, [authStore?.stateItems]);

  useEffect(() => {
    if (apiStates.length === 0) return;
    const stateData = apiStates.find((s) => s.name === state);
    setApiCities([]);
    if (!stateData) return;
    let cancelled = false;
    dispatch(getAllCities({ search_state: stateData.code }))
      .then((action) => {
        if (cancelled) return;
        const cities =
          action.payload?.data?.cities || action.payload?.cities || [];
        setApiCities(cities.map((c) => c.city));
      })
      .catch(() => {
        if (!cancelled) setApiCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [state, apiStates, dispatch]);

  useEffect(() => {
    if (!authStore?.roleItems) return;

    const currentPlan = authStore?.user?.plan?.name
      ? authStore?.user?.plan?.name
      : authStore?.profileItem?.data?.plan?.name;

    const data = authStore.roleItems.describe_roles.map((s) => {
      const roleName = s.name;
      let isDisabled = false;
      let requiredPlan = "";

      if (currentPlan === "Free") {
        if (!allowedRolesForFree.includes(roleName)) {
          isDisabled = true;
          requiredPlan = "Pro";
        }
      }

      return {
        value: s.id,
        label: roleName,
        isDisabled,
        requiredPlan,
        roleName,
      };
    });

    setRoleOptions(data);
  }, [authStore?.roleItems, authStore?.user?.plan?.name, profileStore]);

  const handleDisabledRoleClick = (option) => {
    const requiredPlan = option.requiredPlan;
    setUpgradeSource("nursing_role_limit");
    if (requiredPlan === "Pro") {
      setSubText({
        header: "Subscription Required!",
        desc: "",
        massage1: "",
        massage2: "",
        desc2: "You need a Pro plan to unlock this role",
        promassage:
          "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
        propricem: "$2.99/month",
        propricey: "$29.99/year",
        plan: "Pro",
        lockedItemName: option.roleName,
      });
    }
    setSubAlertCompModalVisible(true);
  };

  // Menus are portaled to document.body, so page-local --mc-* tokens
  // wouldn't resolve there. The global --mmp-* tokens (defined on :root /
  // [data-theme]) do reach the portal.
  const mcSelectStyles = {
    control: (base, st) => ({
      ...base,
      minHeight: "42px",
      borderRadius: "10px",
      borderColor: st.isFocused ? "var(--mmp-accent)" : "var(--mmp-border)",
      boxShadow: st.isFocused ? "0 0 0 3px var(--mmp-accent-bg)" : "none",
      fontSize: "14px",
      fontWeight: 600,
      backgroundColor: "var(--mmp-surface)",
      ":hover": { borderColor: "var(--mmp-accent)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    singleValue: (base) => ({ ...base, color: "var(--mmp-text-primary)", fontWeight: 600 }),
    placeholder: (base) => ({ ...base, color: "var(--mmp-text-secondary)", fontWeight: 500 }),
    menu: (base) => ({
      ...base,
      borderRadius: "10px",
      marginTop: "4px",
      backgroundColor: "var(--mmp-surface)",
      padding: "6px",
      zIndex: 1000,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, st) => ({
      ...base,
      borderRadius: "8px",
      padding: "6px 10px",
      lineHeight: 1.3,
      backgroundColor: st.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
      color: st.isDisabled ? "var(--mmp-text-secondary)" : "var(--mmp-text-primary)",
      fontSize: "13px",
      fontWeight: 500,
      cursor: st.isDisabled ? "pointer" : "default",
      "&:active": { backgroundColor: "var(--mmp-hover-bg)" },
    }),
  };

  const CustomRoleOption = (props) => {
    const { isDisabled, data } = props;
    return (
      <div
        onClick={(e) => {
          if (isDisabled) {
            e.stopPropagation();
            handleDisabledRoleClick(data);
          }
        }}
        style={{ width: "100%", cursor: isDisabled ? "pointer" : "default" }}
      >
        <components.Option {...props}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <span style={{ color: isDisabled ? "var(--mmp-text-secondary)" : "inherit" }}>
              {data.label}
            </span>
            {data.requiredPlan && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: "9px",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--mmp-accent)",
                  background: "var(--mmp-accent-bg)",
                  border: "1px solid var(--mmp-accent-border)",
                  borderRadius: "999px",
                  padding: "1px 6px",
                }}
              >
                {data.requiredPlan}
              </span>
            )}
          </div>
        </components.Option>
      </div>
    );
  };

  const stateSelectOptions = apiStates.map((s) => ({
    value: s.name,
    label: s.name,
  }));

  const citySelectOptions = apiCities.map((c) => ({ value: c, label: c }));

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  const fetchScenarios = () => {
    mortgageScenarioService
      .list()
      .then((res) => {
        const payload = res?.data?.data || {};
        setScenarios(payload.scenarios || []);
        setScenarioPlan({
          plan_slug: payload.plan_slug || "free",
          plan_limit:
            typeof payload.plan_limit === "number" ? payload.plan_limit : 1,
          total_saved: payload.total_saved || 0,
          over_plan_count: payload.over_plan_count || 0,
        });
      })
      .catch(() => setScenarios([]));
  };

  const openScenarioLimitModal = (planSlug, limitType) => {
    const isPro = (planSlug || "").toLowerCase() === "pro";
    setUpgradeSource("mortgage_scenario_limit");
    if (limitType === "daily") {
      // Free plan: 1 mortgage calculation saved per day.
      setSubText({
        header: "Daily Save Limit Reached",
        desc:
          "Free plans can save 1 scenarios per day.",
        massage1:
          "Pro users can save up to 25 scenarios to compare cities side-by-side.",
        pluspricem: "$6.99/month",
        pluspricey: "$69.99/year",
        desc2: "",
        promassage: "",
        propricem: "$2.99/month",
        propricey: "$29.99/year",
        plan: "Pro",
        lockedItemName: "Saved scenarios",
      });
    } else if (isPro) {
      setSubText({
        header: "Saved Scenario Limit Reached",
        desc:
          "You've saved the maximum number of scenarios on Pro. Delete one to save a new city.",
        massage1: "",
        pluspricem: "",
        pluspricey: "",
        promassage: "",
        propricem: "",
        propricey: "",
        plan: "Pro",
        lockedItemName: "Saved scenarios",
      });
    } else {
      setSubText({
        header: "Saved Scenario Limit Reached",
        desc:
          "Delete this one to save a new city, or upgrade to Pro for unlimited scenarios.",
        massage1:
          "Pro users can save up to 25 scenarios to compare cities side-by-side.",
        pluspricem: "$6.99/month",
        pluspricey: "$69.99/year",
        desc2: "",
        promassage: "",
        propricem: "$2.99/month",
        propricey: "$29.99/year",
        plan: "Pro",
        lockedItemName: "Saved scenarios",
      });
    }
    setSubAlertCompModalVisible(true);
  };

  // Free plans can view only their first saved scenario. Tapping a locked
  // scenario card opens the upgrade modal instead of loading it.
  const openScenarioLockedModal = () => {
    setUpgradeSource("mortgage_scenario_limit");
    setSubText({
      header: "Upgrade to view this scenario",
      desc:
        "Free plans can view 1 saved scenario. Upgrade to Pro to unlock and view all your saved scenarios.",
      massage1: "Pro users can view all their saved scenarios.",
      pluspricem: "$6.99/month",
      pluspricey: "$69.99/year",
      desc2: "",
      promassage: "",
      propricem: "$2.99/month",
      propricey: "$29.99/year",
      plan: "Pro",
      // Leave empty so the modal uses the custom `header` above instead of
      // the auto-generated "<name> is a Pro role" heading.
      lockedItemName: "",
    });
    setSubAlertCompModalVisible(true);
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  useEffect(() => {
    const scenarioId = location.state?.scenarioId;
    if (!scenarioId || scenarios.length === 0 || autoLoadedRef.current) return;
    const match = scenarios.find((s) => s.id === scenarioId);
    if (match) {
      autoLoadedRef.current = true;
      loadScenario(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, location.state]);

  useEffect(() => {
    if (apiCities.length > 0 && !apiCities.includes(city)) {
      setCity(apiCities[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCities]);

  const apiStateCode = apiStates.find((s) => s.name === state)?.code;

  useEffect(() => {
    if (!city || !apiStateCode || !role) return;
    searchService
      .getMortgageCityData({ city, state: apiStateCode, role })
      .then((res) => {
        const d = res?.data?.data;
        if (d) {
          setCityData(d);
          if (skipPriceResetRef.current) {
            skipPriceResetRef.current = false;
          } else {
            setPrice(Number(d.median_home_price) || 0);
          }
        } else {
          setCityData(DEFAULT_CITY_DATA);
          if (!skipPriceResetRef.current) setPrice(0);
          else skipPriceResetRef.current = false;
        }
      })
      .catch(() => {
        setCityData(DEFAULT_CITY_DATA);
        if (!skipPriceResetRef.current) setPrice(0);
        else skipPriceResetRef.current = false;
      });
  }, [city, apiStateCode, role]);

  const downPayment = Math.round((price * dpPct) / 100);
  const principal = price - downPayment;
  const r = rate / 100 / 12;
  const n = term * 12;
  const pi =
    r > 0
      ? Math.round(
          (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1),
        )
      : Math.round(principal / n);
  const propTax = Math.round((price * 0.011) / 12);
  const insurance = Math.round((price * 0.0035) / 12);
  const total = pi + propTax + insurance;

  const currentSnapshot = () => ({
    city,
    state: apiStateCode || null,
    describe_role_id: role || null,
    home_price: Number(price) || 0,
    down_payment_pct: Number(dpPct) || 0,
    loan_term_years: Number(term) || 0,
    interest_rate: Number(rate) || 0,
    monthly_payment: Number(total) || 0,
  });

  const isDirty = (() => {
    if (!activeScenario) return false;
    const s = currentSnapshot();
    return (
      s.city !== activeScenario.city ||
      s.state !== activeScenario.state ||
      Number(activeScenario.describe_role_id) !== Number(s.describe_role_id) ||
      Number(activeScenario.home_price) !== s.home_price ||
      Number(activeScenario.down_payment_pct) !== s.down_payment_pct ||
      Number(activeScenario.loan_term_years) !== s.loan_term_years ||
      Number(activeScenario.interest_rate) !== s.interest_rate
    );
  })();

  const createScenario = async () => {
    if (!city || !apiStateCode) return;
    setScenarioSaving(true);
    try {
      const res = await mortgageScenarioService.create(currentSnapshot());
      const saved = res?.data?.data?.scenario;
      if (saved) setActiveScenario(saved);
      fetchScenarios();
      showToast("Scenario saved");
    } catch (e) {
      const body = e?.response?.data;
      if (
        e?.response?.status === 403 &&
        body?.errorType === "MORTGAGE_SCENARIO_LIMIT_REACHED"
      ) {
        openScenarioLimitModal(body?.data?.plan_slug, body?.data?.limit_type);
      } else {
        showToast("Could not save scenario");
      }
    } finally {
      setScenarioSaving(false);
    }
  };

  const updateScenario = async () => {
    if (!activeScenario) return;
    setScenarioSaving(true);
    try {
      const res = await mortgageScenarioService.update(
        activeScenario.id,
        currentSnapshot(),
      );
      const saved = res?.data?.data?.scenario;
      if (saved) setActiveScenario(saved);
      fetchScenarios();
      showToast("Scenario updated");
    } catch (e) {
      showToast("Could not update scenario");
    } finally {
      setScenarioSaving(false);
    }
  };

  const loadScenario = (s) => {
    skipPriceResetRef.current = true;
    const fullState =
      apiStates.find((st) => st.code === s.state)?.name || s.state;
    setRole(Number(s.describe_role_id) || role);
    setState(fullState);
    setCity(s.city);
    setPrice(Number(s.home_price) || 0);
    setDpPct(Number(s.down_payment_pct) || 0);
    setTerm(Number(s.loan_term_years) || 30);
    setRate(Number(s.interest_rate) || 0);
    setActiveScenario(s);

    mortgageScenarioService
      .touchView(s.id)
      .then(() => fetchScenarios())
      .catch(() => {});
  };

  const deleteScenario = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await mortgageScenarioService.remove(id);
      if (activeScenario?.id === id) setActiveScenario(null);
      fetchScenarios();
      showToast("Scenario removed");
    } catch (err) {
      showToast("Could not remove scenario");
    }
  };

  const grossAnnual = Number(cityData?.gross_annual_salary) || 0;
  const netAnnualFromApi = Number(cityData?.net_annual_salary) || 0;
  const taxRate = Number(cityData?.tax_rate) || 0;
  const monthlyRent = Number(cityData?.monthly_rent) || 0;
  const netMonthly =
    netAnnualFromApi > 0
      ? Math.round(netAnnualFromApi / 12)
      : Math.round((grossAnnual * (1 - taxRate)) / 12);
  const pctOfPay = netMonthly > 0 ? Math.round((total / netMonthly) * 100) : 0;
  const aff = pctOfPay < 30 ? "good" : pctOfPay < 40 ? "warn" : "bad";
  const affMsg = {
    good: "Comfortably affordable",
    warn: "Stretching the budget",
    bad: "Likely unaffordable",
  }[aff];

  const rentVsOwn = total - monthlyRent;

  const getRedfinZipUrl = (zip) => {
    if (!zip) return "https://www.redfin.com";
    return `https://www.redfin.com/zipcode/${zip}`;
  };

  const openRedfin = () => {
    window.open(getRedfinZipUrl(cityData?.zip), "_blank", "noreferrer");
  };

  // Mirrors SearchDetail.js getZillowLink: geocodes the city, then opens
  // Zillow's for_sale search with a bounding-box around the centroid.
  const openZillow = async () => {
    if (!city || !apiStateCode) return;
    const query = `${city.toLowerCase()}, ${apiStateCode.toLowerCase()}`;
    const radiusMiles = 20;
    try {
      const geoRes = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query,
        )}&format=json&limit=1`,
      );
      const geo = await geoRes.json();
      if (!geo?.length) throw new Error("Location not found");

      const lat = parseFloat(geo[0].lat);
      const lon = parseFloat(geo[0].lon);
      const latOffset = radiusMiles / 69;
      const lonOffset = radiusMiles / (69 * Math.cos((lat * Math.PI) / 180));

      const saleSearchState = {
        pagination: {},
        mapBounds: {
          north: lat + latOffset,
          south: lat - latOffset,
          east: lon + lonOffset,
          west: lon - lonOffset,
        },
        isMapVisible: true,
        isListVisible: true,
        mapZoom: 9,
        filterState: {
          isAllHomes: { value: true },
          sortSelection: { value: "globalrelevance" },
        },
      };

      const url = `https://www.zillow.com/homes/for_sale/?searchQueryState=${encodeURIComponent(
        JSON.stringify(saleSearchState),
      )}`;
      window.open(url, "_blank", "noreferrer");
    } catch (e) {
      window.open("https://www.zillow.com", "_blank", "noreferrer");
    }
  };

  return (
    <>
      <NoIndexSEO title={seoConfig.mortgage.title} />
      <div className="mortgage-calc-page">
        <div className="mc-shell">
          {activeProfileNudge && !profileNudgeDismissed && (
            <div
              className="mc-profile-nudge"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/profile")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/profile");
                }
              }}
            >
              <div className="mc-profile-nudge-icon">
                <UserRound />
              </div>
              <div className="mc-profile-nudge-body">
                <div className="mc-profile-nudge-head">
                  <span className="mc-profile-nudge-eyebrow">
                    Complete your profile
                  </span>
                  <span className="mc-profile-nudge-pct">
                    {profileCompletionPercent}%
                  </span>
                </div>
                <div className="mc-profile-nudge-bar-track">
                  <div
                    className="mc-profile-nudge-bar-fill"
                    style={{ width: `${profileCompletionPercent}%` }}
                  />
                </div>
                <span className="mc-profile-nudge-text">
                  {activeProfileNudge.message}
                </span>
              </div>
              <button
                type="button"
                className="mc-profile-nudge-close"
                aria-label="Dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissProfileNudge();
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Title */}
          <div className="mc-title-row">
            <div className="mc-title-line">
              <span className="mc-title-icon">
                <Icon.home />
              </span>
              <h1 className="mc-title">Mortgage Calculator</h1>
              <InfoTooltip text="Estimates your monthly mortgage payment and affordability using the home price, down payment, loan term, and rate you set below." />
            </div>
            <div className="mc-subtitle">
              See what nurses can actually afford in any city.
            </div>
          </div>

          {/* Saved Scenarios */}
          {(scenarios.length > 0 || scenarioPlan.over_plan_count > 0) && (
            <div className="mc-scenarios-block" data-tour="mc-saved">
              <div className="mc-section-label">
                Saved scenarios
                <InfoTooltip text="Save your mortgage calculation to quickly reload and compare different home-buying scenarios later." />
              </div>
              {scenarioPlan.over_plan_count > 0 && (
                <div className="mc-scenarios-banner">
                  You have{" "}
                  <b className="t-num">{scenarioPlan.over_plan_count}</b>{" "}
                  scenario
                  {scenarioPlan.over_plan_count === 1 ? "" : "s"} saved beyond
                  your current plan.{" "}
                  <button
                    type="button"
                    className="mc-scenarios-banner-cta"
                    onClick={() => navigate("/profile/subscription")}
                  >
                    Upgrade to Pro
                  </button>{" "}
                  to access them again.
                </div>
              )}
              {scenarios.length > 0 && (
                <div className="mc-scenarios">
                  {scenarios.map((s, index) => {
                    const monthly = Math.round(Number(s.monthly_payment) || 0);
                    const isActive = activeScenario?.id === s.id;
                    const displayName = s.custom_name || s.city;
                    // Free plans can only view the first `plan_limit` scenarios;
                    // the rest are locked behind an upgrade.
                    const isFreePlan =
                      (scenarioPlan.plan_slug || "").toLowerCase() === "free";
                    // Always keep at least the first scenario viewable for
                    // free users, even if the API reports a smaller limit.
                    const planLimit = Math.max(
                      typeof scenarioPlan.plan_limit === "number"
                        ? scenarioPlan.plan_limit
                        : 1,
                      1,
                    );
                    const isLocked = isFreePlan && index >= planLimit;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() =>
                          isLocked ? openScenarioLockedModal() : loadScenario(s)
                        }
                        className={`mc-scenario-card${
                          isActive ? " is-active" : ""
                        }${isLocked ? " is-locked" : ""}`}
                        aria-label={
                          isLocked
                            ? `${displayName}, ${s.state} — locked, upgrade to view`
                            : undefined
                        }
                      >
                        {!isLocked && (
                          <span
                            className="mc-scenario-remove"
                            role="button"
                            tabIndex={0}
                            aria-label="Remove scenario"
                            onClick={(e) => deleteScenario(s.id, e)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                deleteScenario(s.id, e);
                              }
                            }}
                          >
                            ×
                          </span>
                        )}
                        <div className="mc-scenario-city">
                          {displayName}, {s.state}
                        </div>
                        <div className="mc-scenario-amount t-num">
                          ${monthly.toLocaleString()}
                          <span className="mc-scenario-per">/mo</span>
                        </div>
                        <div className="mc-scenario-cta">
                          {isLocked ? (
                            <span className="mc-scenario-locked-cta">
                              <Icon.lock className="mc-scenario-lock-icon" />
                              Upgrade to view
                            </span>
                          ) : isActive ? (
                            "Loaded"
                          ) : (
                            "Tap to load →"
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="mc-grid">
            {/* Left column: input card */}
            <div className="mc-col mc-col-left">
              <div className="mc-card mc-input-card" data-tour="mc-inputcard">
                <div className="m-dropdown" data-tour="mc-role">
                  <div className="m-label">Nursing Role</div>
                  <Select
                    aria-label="Nursing Role"
                    value={roleOptions.find((o) => o.value === role) || null}
                    onChange={(opt) => setRole(opt?.value || null)}
                    options={roleOptions}
                    components={{ Option: CustomRoleOption }}
                    isOptionDisabled={(o) => o.isDisabled}
                    isLoading={roleOptions.length === 0}
                    isClearable={false}
                    placeholder="Select Role"
                    styles={mcSelectStyles}
                    classNamePrefix="mc-select"
                  />
                </div>
                <div className="mc-two-col">
                  <div className="m-dropdown" data-tour="mc-state">
                    <div className="m-label">State</div>
                    <Select
                      aria-label="State"
                      value={
                        stateSelectOptions.find((o) => o.value === state) ||
                        (state ? { value: state, label: state } : null)
                      }
                      onChange={(opt) => setState(opt?.value || "")}
                      options={stateSelectOptions}
                      isClearable={false}
                      placeholder="Select State"
                      styles={mcSelectStyles}
                      classNamePrefix="mc-select"
                    />
                  </div>
                  <div className="m-dropdown" data-tour="mc-city">
                    <div className="m-label">City</div>
                    <Select
                      aria-label="City"
                      value={
                        citySelectOptions.find((o) => o.value === city) ||
                        (city ? { value: city, label: city } : null)
                      }
                      onChange={(opt) => setCity(opt?.value || "")}
                      options={citySelectOptions}
                      isClearable={false}
                      placeholder="Select City"
                      styles={mcSelectStyles}
                      classNamePrefix="mc-select"
                    />
                  </div>
                </div>
                <MoneyInput
                  dataTour="mc-price"
                  label={`Typical Home Value for the Region · in ${city}`}
                  value={price}
                  onChange={setPrice}
                />

                {/* Down payment */}
                <div className="mc-block" data-tour="mc-down">
                  <div className="mc-row-between">
                    <span className="m-label m-label-plain">Down Payment</span>
                    <span className="mc-dp-amount t-num">
                      {dpPct}% · ${downPayment.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="3"
                    max="50"
                    step="1"
                    value={dpPct}
                    onChange={(e) => setDpPct(parseInt(e.target.value))}
                    className="mc-range"
                    style={{
                      "--mc-range-fill": `${((dpPct - 3) / (50 - 3)) * 100}%`,
                    }}
                  />
                  <div className="mc-range-ticks">
                    <span style={{ left: "0%" }}>3%</span>
                    <span style={{ left: `${((20 - 3) / (50 - 3)) * 100}%` }}>
                      20%
                    </span>
                    <span style={{ left: "100%" }}>50%</span>
                  </div>
                </div>

                {/* Loan term */}
                <div className="mc-block" data-tour="mc-term">
                  <div className="m-label">Loan Term</div>
                  {/* data-active drives the sliding pill in mortgage.scss
                    (0 = first option, 1 = second option). */}
                  <div
                    className="mc-segmented"
                    data-active={term === 30 ? 1 : 0}
                  >
                    <button
                      type="button"
                      className={`mc-seg ${term === 15 ? "is-active" : ""}`}
                      onClick={() => setTerm(15)}
                    >
                      15 years
                    </button>
                    <button
                      type="button"
                      className={`mc-seg ${term === 30 ? "is-active" : ""}`}
                      onClick={() => setTerm(30)}
                    >
                      30 years
                    </button>
                  </div>
                </div>

                {/* Rate */}
                <div className="mc-block" data-tour="mc-rate">
                  <div className="m-label">Interest Rate</div>
                  <div className="mc-rate-box">
                    <input
                      type="number"
                      step="0.05"
                      value={rate}
                      onChange={(e) => setRate(parseFloat(e.target.value) || 0)}
                      className="mc-rate-input"
                    />
                    <span className="mc-rate-suffix">% APR</span>
                  </div>
                  <div className="mc-rate-hint">
                    National avg today · 7.065%
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: results card */}
            <div className="mc-col mc-col-right">
              <div className="mc-card mc-results-card" data-tour="mc-results">
                <div className="mc-results-label">
                  Your Estimated Monthly Payment
                  <InfoTooltip text="Calculated from the home price, down payment, loan term, and interest rate you set on the left, including estimated property tax and home insurance." />
                </div>
                <div className="mc-results-amount t-num" data-tour="mc-total">
                  ${total.toLocaleString()}
                  <span className="mc-results-per">/mo</span>
                </div>

                {/* Breakdown */}
                <div className="mc-breakdown" data-tour="mc-totalrow">
                  {[
                    ["Principal & Interest", pi],
                    ["Property Tax (est.)", propTax],
                    ["Home Insurance (est.)", insurance],
                  ]
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => (
                    <div key={k} className="mc-breakdown-row">
                      <span className="mc-breakdown-k">{k}</span>
                      <span className="mc-breakdown-v t-num">
                        ${v.toLocaleString()}/mo
                      </span>
                    </div>
                  ))}
                  <div className="mc-breakdown-row mc-breakdown-total">
                    <span>Total Monthly</span>
                    <span className="t-num">${total.toLocaleString()}/mo</span>
                  </div>
                </div>

                {/* Affordability */}
                <div
                  className={`mc-affordability mc-aff-${aff}`}
                  data-tour="mc-afford"
                >
                  <div className="mc-aff-head">
                    <span className="mc-aff-title">
                      Can you afford this?
                      <InfoTooltip text="Based on the mortgage's share of your monthly take-home pay: under 30% is comfortably affordable, 30-40% is stretching the budget, 40%+ is likely unaffordable." />
                    </span>
                    <span className="mc-aff-msg">{affMsg}</span>
                  </div>
                  <div className="mc-aff-bar">
                    <div
                      className="mc-aff-bar-fill"
                      style={{ width: `${Math.min(pctOfPay, 100)}%` }}
                    />
                  </div>
                  <div className="mc-aff-text">
                    Mortgage takes up{" "}
                    <b className="t-num mc-aff-pct">{pctOfPay}%</b> of your{" "}
                    <span className="t-num">
                      ${netMonthly.toLocaleString()}
                    </span>
                    /mo take-home pay.
                  </div>
                </div>

                {/* Affiliate */}
                <a
                  href="https://www.sofi.com/multi/6726019/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mc-affiliate"
                  data-tour="mc-sofi"
                >
                  <div className="mc-affiliate-row">
                    <div className="mc-affiliate-logo">BR</div>
                    <div className="mc-affiliate-text">
                      <div className="mc-affiliate-title">
                        Get pre-qualified for a mortgage
                        <InfoTooltip text="Check your mortgage pre-qualification with SoFi. Your rate check won't affect your credit score." />
                      </div>
                      <div className="mc-affiliate-desc">
                        Compare rates from top lenders without affecting your
                        credit score
                      </div>
                    </div>
                    <span className="mc-affiliate-cta">Learn more →</span>
                  </div>
                  <div className="mc-affiliate-sponsor">Sponsored by SoFi</div>
                </a>

                {/* Down payment required */}
                <div className="mc-dp-required" data-tour="mc-downreq">
                  <div>
                    <div className="m-label m-label-brand">
                      Down Payment Required
                      <InfoTooltip text="The down payment percentage you set above, applied to the home price shown here." />
                    </div>
                    <div className="mc-dp-required-sub">
                      {dpPct}% of ${price.toLocaleString()}
                    </div>
                  </div>
                  <div className="mc-dp-required-amt t-num">
                    ${downPayment.toLocaleString()}
                  </div>
                </div>

                {/* Save / Update scenario buttons */}
                <div data-tour="mc-savebtn">
                {activeScenario && isDirty && (
                  <div className="mc-save-actions">
                    <button
                      type="button"
                      onClick={updateScenario}
                      disabled={scenarioSaving}
                      className="mc-save-btn mc-save-btn-primary"
                    >
                      <Icon.bookmark className="mc-save-icon" />
                      <span>
                        {scenarioSaving ? "Saving…" : "Update scenario"}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={createScenario}
                      disabled={scenarioSaving}
                      className="mc-save-btn mc-save-btn-secondary"
                    >
                      <span>Save as new</span>
                    </button>
                  </div>
                )}
                {activeScenario && !isDirty && (
                  <button
                    type="button"
                    disabled
                    className="mc-save-btn mc-save-btn-saved"
                    aria-label="Scenario saved"
                  >
                    <Icon.bookmark className="mc-save-icon" />
                    <span>Saved</span>
                  </button>
                )}
                {!activeScenario && (
                  <button
                    type="button"
                    onClick={createScenario}
                    disabled={scenarioSaving || !city || !apiStateCode}
                    className="mc-save-btn"
                  >
                    <Icon.bookmark className="mc-save-icon" />
                    <span>{scenarioSaving ? "Saving…" : "Save scenario"}</span>
                  </button>
                )}
                </div>
              </div>
            </div>
          </div>

          {/* Rent vs Own */}
          <div className="mc-card mc-rent-own" data-tour="mc-rentown">
            <div className="mc-rent-own-title">
              Renting vs Owning · {city}
              <InfoTooltip text="Compares the median 1-bedroom rent in this city against your full estimated mortgage payment above." />
            </div>
            <div className="mc-rent-own-rows">
              {[
                ["Rent (1-bed median)", monthlyRent, "var(--mc-brand-500)"],
                ["Own (full mortgage)", total, "var(--mc-brand-700)"],
              ].map(([k, v, c], bi) => {
                const max = Math.max(monthlyRent, total);
                const pct = max > 0 ? (v / max) * 100 : 0;
                return (
                  <div
                    key={k}
                    data-tour={bi === 0 ? "mc-rentbar" : "mc-ownbar"}
                  >
                    <div className="mc-rent-row-head">
                      <span className="mc-rent-row-k">{k}</span>
                      <span className="mc-rent-row-v t-num">
                        ${v.toLocaleString()}/mo
                      </span>
                    </div>
                    <div className="mc-rent-bar">
                      <div
                        className="mc-rent-bar-fill"
                        style={{ width: `${pct}%`, background: c }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mc-rent-summary">
              {rentVsOwn > 0 ? (
                <>
                  Owning costs{" "}
                  <b className="t-num mc-rent-warn">
                    ${rentVsOwn.toLocaleString()}/mo more
                  </b>{" "}
                  than renting here.
                </>
              ) : (
                <>
                  Owning saves{" "}
                  <b className="t-num mc-rent-good">
                    ${Math.abs(rentVsOwn).toLocaleString()}/mo
                  </b>{" "}
                  vs renting here.
                </>
              )}
            </div>
          </div>

          {/* CTAs */}
          <div className="mc-ctas" data-tour="mc-ctas">
            <button
              type="button"
              onClick={openZillow}
              disabled={!city || !apiStateCode}
              className="mc-cta"
              data-tour="mc-zillow"
            >
              <div className="mc-cta-head">
                <span className="mc-cta-name" style={{ color: "var(--mc-brand-zillow)" }}>
                  Zillow
                </span>
                <Icon.ext className="mc-cta-icon" />
              </div>
              <span className="mc-cta-sub">Homes for sale in {city}</span>
            </button>
            <button
              type="button"
              onClick={openRedfin}
              disabled={!cityData?.zip}
              className="mc-cta"
              data-tour="mc-redfin"
            >
              <div className="mc-cta-head">
                <span className="mc-cta-name" style={{ color: "var(--mc-brand-redfin)" }}>
                  Redfin
                </span>
                <Icon.ext className="mc-cta-icon" />
              </div>
              <span className="mc-cta-sub">
                {cityData?.zip
                  ? `Homes for sale in ${city}`
                  : "ZIP unavailable — open Redfin"}
              </span>
            </button>
          </div>

          {/* Data source disclaimer — full width below the Zillow/Redfin CTAs */}
          <div className="zillow-data-disclaimer">
            <div className="zillow-data-disclaimer__title">Data Disclaimer</div>
            <p>
              Housing data sourced from{" "}
              <a
                href="https://www.zillow.com/research/data/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Zillow Research
              </a>
              .
            </p>
            <p>
              Home values shown represent the typical home value for the region
              as reported by Zillow&apos;s Home Value Index and are not
              statistical medians. Rental data is sourced from Zillow&apos;s
              Observed Rent Index. Data is refreshed periodically and may not
              reflect real-time market conditions.
            </p>
          </div>

          {toast && <div className="mc-toast">{toast}</div>}

          <ComparisonModal
            title={subText?.header}
            description={subText.desc}
            massage1={subText.massage1}
            pluspricem={subText.pluspricem}
            pluspricey={subText.pluspricey}
            prodesc={subText.desc2}
            promassage={subText.promassage}
            propricem={subText.propricem}
            propricey={subText.propricey}
            plan={subText.plan}
            lockedItemName={subText.lockedItemName}
            eventSource={upgradeSource}
            isVisible={subAlertCompModalVisible}
            onClose={() => setSubAlertCompModalVisible(false)}
            onPressYes={() => {
              navigate("/profile/subscription");
              setSubAlertCompModalVisible(false);
            }}
            yesText="Upgrade Now"
            noText="Cancel"
            extraMetaInfo={true}
          />
        </div>
      </div>

      <GuidedTour
        isOpen={isTourOpen}
        steps={mortgageTourSteps}
        onDone={finishTour}
      />
    </>
  );
}
